import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCart } from "../../context/CartContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useVendorStore } from "../../context/VendorStoreContext";
import CouponPage, { getStoredCheckoutCouponCode } from "./CouponPage";
import { apiClient } from "../../services/apiClientService";
import ShippingInfo from "./ShippingInfo";
import PaymentMethod from "./PaymentMethod";
import OrderSummary from "./OrderSummary";
import { v4 as uuidv4 } from "uuid";
import {
  ChevronDown,
  Clock,
  CreditCard,
  MapPin,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Truck,
} from "lucide-react";
import { getLineItemUnitPrice } from "../../utils/productVariants";
import { useUserAuth } from "../../context/UserAuthContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import {
  trackBeginCheckout,
  trackOrderCompleted,
} from "../../analytics/events";

function orderLineDisplayName(product, cartItem) {
  const vId = cartItem?.variantOptionId;
  if (!vId) return product.name;
  const opt = product.variantGroup?.options?.find(
    (o) => String(o.optionId) === String(vId),
  );
  const gName = product.variantGroup?.name || "Option";
  if (opt?.label) return `${product.name} (${gName}: ${opt.label})`;
  return product.name;
}

const sectionHeaderClass =
  "flex w-full items-center justify-between gap-3 rounded-t-2xl px-4 py-3.5 text-left sm:px-5";

const CheckoutPage = () => {
  const orderRef = useRef(null);
  const shippingRef = useRef(null);
  const paymentRef = useRef(null);

  const { isAuthenticated } = useUserAuth();
  const [storeName, setStoreName] = useState("Your Shop Name");
  const [fulfillmentMeta, setFulfillmentMeta] = useState({
    label: "",
    description: "",
  });
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    country: "India",
    state: "",
    city: "",
    address: "",
  });

  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [isNewAddress, setIsNewAddress] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [activeSection, setActiveSection] = useState("order");
  /** Keeps delivery panel mounted after first open so GET /shipping-addresses is not repeated every toggle. */
  const [shippingPanelRetained, setShippingPanelRetained] = useState(false);
  /** `pickup` = no address required; cart delivery fees waived for this checkout. */
  const [receivingMethod, setReceivingMethod] = useState("delivery");
  /** `delivery` | `pickup` | `both` — from storefront vendor; `both` enables pickup choice. */
  const [customerFulfillmentMode, setCustomerFulfillmentMode] =
    useState("delivery");
  const [pickupPointLocation, setPickupPointLocation] = useState("");
  const [pickupPointGoogleMapsUrl, setPickupPointGoogleMapsUrl] = useState("");
  const [pickupPointLat, setPickupPointLat] = useState(null);
  const [pickupPointLng, setPickupPointLng] = useState(null);
  const { cart, updateQuantity, clearCart } = useCart();
  const { codEnabled, razorpayEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const location = useLocation();
  const { storeSlug } = useVendorStore();
  const shopPath = storeSlug ? `/${storeSlug}` : "/shop";
  const successPath = storeSlug ? `/${storeSlug}/order-success` : "/order-success";

  /** True when every item in the cart is a service package. */
  const isServiceCart = useMemo(
    () => cart.length > 0 && cart.every((i) => i.product?.productType === "service"),
    [cart],
  );

  const checkoutVendorId = useMemo(() => {
    if (!cart.length) return "";
    const v = cart[0]?.product?.vendorId;
    if (v == null) return "";
    if (typeof v === "object" && v._id != null) return String(v._id);
    return String(v);
  }, [cart]);

  useEffect(() => {
    let cancelled = false;
    const loadStorefront = async () => {
      try {
        const qs = checkoutVendorId ? `?vendorId=${checkoutVendorId}` : "";
        const res = await apiClient("GET", `/api/vendors/storefront${qs}`, null, {
          skipLoading: true,
        });
        const vendorDoc = res?.vendor;
        const n = vendorDoc?.storeName || vendorDoc?.name;
        if (!cancelled && n) setStoreName(String(n));
        if (!cancelled && vendorDoc) {
          setFulfillmentMeta({
            label: String(vendorDoc.fulfillmentTermLabel || "").trim(),
            description: String(vendorDoc.fulfillmentTimeDescription || "").trim(),
          });
          setCustomerFulfillmentMode(
            String(vendorDoc.customerFulfillmentMode || "delivery").toLowerCase(),
          );
          setPickupPointLocation(String(vendorDoc.pickupPointLocation || "").trim());
          setPickupPointGoogleMapsUrl(
            String(vendorDoc.pickupPointGoogleMapsUrl || "").trim(),
          );
          setPickupPointLat(
            typeof vendorDoc.pickupPointLat === "number" ? vendorDoc.pickupPointLat : null,
          );
          setPickupPointLng(
            typeof vendorDoc.pickupPointLng === "number" ? vendorDoc.pickupPointLng : null,
          );
        } else if (!cancelled) {
          setCustomerFulfillmentMode("delivery");
          setPickupPointLocation("");
          setPickupPointGoogleMapsUrl("");
          setPickupPointLat(null);
          setPickupPointLng(null);
        }
      } catch {
        // Keep default "Your Shop Name"
      }
    };
    loadStorefront();
    return () => {
      cancelled = true;
    };
  }, [checkoutVendorId]);

  useEffect(() => {
    if (isServiceCart) {
      setReceivingMethod("service");
      return;
    }
    if (customerFulfillmentMode === "pickup") setReceivingMethod("pickup");
    else if (customerFulfillmentMode === "delivery") setReceivingMethod("delivery");
    else setReceivingMethod("delivery");
  }, [customerFulfillmentMode, isServiceCart]);

  const subtotal = cart.reduce(
    (sum, { product, quantity, variantOptionId }) =>
      sum + getLineItemUnitPrice(product, variantOptionId) * quantity,
    0,
  );
  const gstTotal = 0;

  const itemCount = useMemo(
    () => cart.reduce((n, { quantity }) => n + quantity, 0),
    [cart],
  );

  /** Set on each product by the API when the vendor has approved `mmd:online-payment`. */
  const vendorOnlineAllowed = useMemo(
    () => cart[0]?.product?.vendorAcceptsOnlinePayment === true,
    [cart],
  );

  /** One delivery & packing charge per order: max of each product's fee (not summed per qty or per line). */
  const shippingFee = useMemo(() => {
    if (cart.length === 0 || isServiceCart) return 0;
    return cart.reduce((max, { product }) => {
      const raw = Number(product?.shippingFee ?? 0);
      const fee = Number.isFinite(raw) ? Math.max(0, raw) : 0;
      return Math.max(max, fee);
    }, 0);
  }, [cart, isServiceCart]);

  const effectiveShippingFee =
    isServiceCart || receivingMethod === "pickup" ? 0 : shippingFee;

  /** Payable before coupon; minimum final total is ₹1, so max discount is this minus 1. */
  const maxCouponDiscount = useMemo(
    () => Math.max(0, subtotal + effectiveShippingFee + gstTotal - 1),
    [subtotal, effectiveShippingFee, gstTotal],
  );

  const effectiveCouponDiscount = useMemo(
    () => Math.min(couponDiscount, maxCouponDiscount),
    [couponDiscount, maxCouponDiscount],
  );

  const finalPayableRupees = useMemo(
    () =>
      Math.max(
        1,
        subtotal + effectiveShippingFee + gstTotal - effectiveCouponDiscount,
      ),
    [subtotal, effectiveShippingFee, gstTotal, effectiveCouponDiscount],
  );

  useEffect(() => {
    setCouponDiscount((d) => Math.min(d, maxCouponDiscount));
  }, [maxCouponDiscount]);

  const displayTotal = finalPayableRupees;

  const checkoutTrackedRef = useRef(false);
  useEffect(() => {
    if (checkoutTrackedRef.current || cart.length === 0) return;
    checkoutTrackedRef.current = true;
    trackBeginCheckout({
      cartValue: finalPayableRupees,
      itemCount: cart.length,
      vendorId: checkoutVendorId,
      storeSlug,
    });
  }, [cart.length, finalPayableRupees, checkoutVendorId, storeSlug]);

  const checkoutRazorpayEnabled = useMemo(
    () => razorpayEnabled && vendorOnlineAllowed,
    [razorpayEnabled, vendorOnlineAllowed],
  );

  const toggleSection = (key) => {
    setActiveSection((prev) => (prev === key ? "" : key));
  };

  useEffect(() => {
    if (activeSection === "shipping") {
      setShippingPanelRetained(true);
    }
  }, [activeSection]);

  useEffect(() => {
    if (activeSection !== "shipping" || !shippingRef.current) return;
    const t = window.setTimeout(() => {
      shippingRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 120);
    return () => window.clearTimeout(t);
  }, [activeSection]);

  const shippingInfoForOrder = () => ({
    ...form,
    receivingMethod,
    deliveryFeeWaived: receivingMethod === "pickup",
  });

  useEffect(() => {
    if (codEnabled && checkoutRazorpayEnabled) {
      setPaymentMethod((prev) => (prev === "razorpay" ? "razorpay" : "cod"));
      return;
    }
    if (!codEnabled && checkoutRazorpayEnabled) {
      setPaymentMethod("razorpay");
      return;
    }
    if (codEnabled && !checkoutRazorpayEnabled) {
      setPaymentMethod("cod");
      return;
    }
    setPaymentMethod("cod");
  }, [codEnabled, checkoutRazorpayEnabled]);

  const handleOnlinePayment = async () => {
    if (!isAuthenticated) {
      navigate("/user-login", {
        state: { from: `${location.pathname}${location.search}` },
        replace: true,
      });
      return;
    }
    if (!checkoutVendorId) {
      alert("Could not determine the seller for this cart. Please refresh and try again.");
      return;
    }
    if (!razorpayEnabled || !vendorOnlineAllowed) {
      alert(
        "Online payment is not available for this seller. Please pay with cash on delivery or try another shop.",
      );
      return;
    }
    try {
      const amount = finalPayableRupees;

      const order = await apiClient("POST", "/api/razorpay/create-order", {
        amount,
        vendorId: checkoutVendorId,
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: storeName,
        description: "Order Payment",
        order_id: order.id,
        handler: async function (response) {
          const verifyRes = await apiClient(
            "POST",
            "/api/razorpay/verify",
            response,
          );
          if (verifyRes.success) {
            await apiClient("POST", "/api/orders/save", {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              amount: order.amount,
              products: cart.map((item) => ({
                productId: item.product._id,
                name: orderLineDisplayName(item.product, item),
                quantity: item.quantity,
                price: getLineItemUnitPrice(
                  item.product,
                  item.variantOptionId,
                ),
              })),
              shippingInfo: shippingInfoForOrder(),
              appliedCouponCode: getStoredCheckoutCouponCode() || undefined,
            });

            if (isNewAddress && receivingMethod === "delivery") {
              await apiClient("POST", "/api/users/shipping-addresses", form);
            }

            trackOrderCompleted({
              orderId: response.razorpay_order_id,
              value: finalPayableRupees,
              paymentMethod: "razorpay",
              itemCount: cart.length,
              vendorId: checkoutVendorId,
            });

            clearCart();
            await apiClient("POST", "/api/users/cart", { cart: [] });

            navigate(successPath, {
              state: {
                orderId: response.razorpay_order_id,
                amount: order.amount,
                products: cart.map((item) => ({
                  name: orderLineDisplayName(item.product, item),
                  quantity: item.quantity,
                  price: getLineItemUnitPrice(
                    item.product,
                    item.variantOptionId,
                  ),
                })),
                customer: shippingInfoForOrder(),
              },
            });
          } else {
            alert("❌ Payment verification failed.");
          }
        },
        prefill: {
          name: form.fullName || "Customer",
          email: form.email,
          contact: form.phone,
        },
        notes: {
          address:
            receivingMethod === "pickup"
              ? "Self pickup — delivery fee waived"
              : `${form.address}, ${form.city}, ${form.state}, ${form.country}`,
        },
        theme: { color: "#ea580c" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Payment error:", err);
      alert("Payment failed. Please try again.");
    }
  };

  const handleCODPayment = async () => {
    if (!isAuthenticated) {
      navigate("/user-login", {
        state: { from: `${location.pathname}${location.search}` },
        replace: true,
      });
      return;
    }
    if (!codEnabled) {
      alert("Cash on delivery is temporarily unavailable. Please pay online or try later.");
      return;
    }
    const orderId = uuidv4();
    try {
      await apiClient("POST", "/api/orders/save", {
        products: cart.map((item) => ({
          orderId,
          productId: item.product._id,
          name: orderLineDisplayName(item.product, item),
          quantity: item.quantity,
          price: getLineItemUnitPrice(item.product, item.variantOptionId),
        })),
        amount: finalPayableRupees * 100, // paise
        shippingInfo: shippingInfoForOrder(),
        appliedCouponCode: getStoredCheckoutCouponCode() || undefined,
      });

      if (isNewAddress && receivingMethod === "delivery") {
        await apiClient("POST", "/api/users/shipping-addresses", form);
      }

      trackOrderCompleted({
        orderId,
        value: finalPayableRupees,
        paymentMethod: "cod",
        itemCount: cart.length,
        vendorId: checkoutVendorId,
      });

      clearCart();
      await apiClient("POST", "/api/users/cart", { cart: [] });

      navigate(successPath, {
        state: {
          orderId,
          amount: finalPayableRupees * 100,
          products: cart.map((item) => ({
            name: orderLineDisplayName(item.product, item),
            quantity: item.quantity,
            price: getLineItemUnitPrice(item.product, item.variantOptionId),
          })),
          customer: shippingInfoForOrder(),
        },
      });
    } catch (err) {
      console.error("COD payment error:", err);
      alert("Payment failed. Please try again.");
    }
  };

  const needsAddress =
    isServiceCart ||
    customerFulfillmentMode === "delivery" ||
    (customerFulfillmentMode === "both" && receivingMethod === "delivery");

  const canPlaceOrder =
    !needsAddress || !!String(form.address || "").trim();

  const shipStepWord = isServiceCart
    ? "Your Address"
    : fulfillmentMeta.label || "Delivery";

  const steps = [
    { key: "order", label: "Order", done: true },
    {
      key: "shipping",
      label: shipStepWord,
      done: canPlaceOrder,
      active: activeSection === "shipping",
    },
    {
      key: "payment",
      label: "Pay",
      done: false,
      active: activeSection === "payment",
    },
  ];

  const placeOrderLabel =
    !codEnabled && !checkoutRazorpayEnabled
      ? "No payment methods"
      : paymentMethod === "razorpay"
        ? "Pay securely"
        : "Place order";

  const hasPaymentOption = codEnabled || checkoutRazorpayEnabled;

  if (cart.length === 0) {
    return (
      <div className="min-h-[70vh] bg-gradient-to-b from-orange-50 via-amber-50/40 to-stone-100 px-4 py-16">
        <div className="mx-auto max-w-md rounded-3xl border border-stone-200/80 bg-white p-10 text-center shadow-xl shadow-orange-100/50">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
            <ShoppingBag className="h-8 w-8" />
          </div>
          <h1 className="text-xl font-bold text-stone-900">Your bag is empty</h1>
          <p className="mt-2 text-stone-600">
            Add something delicious from the menu, then come back to checkout.
          </p>
          <Link
            to={shopPath}
            className="mt-8 inline-flex items-center justify-center rounded-2xl bg-orange-500 px-8 py-3.5 text-sm font-bold text-white shadow-lg shadow-orange-300/50 transition hover:bg-orange-600"
          >
            Browse menu
          </Link>
        </div>
      </div>
    );
  }

  const MobileAccordion = ({
    sectionKey,
    title,
    subtitle,
    icon: Icon,
    children,
    refProp,
    keepBodyMounted,
  }) => {
    const open = activeSection === sectionKey;
    const showBody = open || keepBodyMounted;
    return (
      <div
        ref={refProp}
        className="overflow-hidden rounded-2xl border border-stone-200/90 bg-white shadow-md shadow-stone-200/40"
      >
        <button
          type="button"
          onClick={() => toggleSection(sectionKey)}
          className={`${sectionHeaderClass} ${open ? "bg-orange-50/60" : "bg-white hover:bg-stone-50/80"}`}
        >
          <span className="flex min-w-0 flex-1 items-center gap-3">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                open ? "bg-orange-500 text-white" : "bg-stone-100 text-stone-600"
              }`}
            >
              <Icon className="h-5 w-5" strokeWidth={2} />
            </span>
            <span className="min-w-0 text-left">
              <span className="block font-bold text-stone-900">{title}</span>
              {subtitle ? (
                <span className="block truncate text-xs text-stone-500">{subtitle}</span>
              ) : null}
            </span>
          </span>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-stone-500 transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
        {showBody ? (
          <div
            className={`border-t border-stone-100 p-4 sm:p-5 ${!open && keepBodyMounted ? "hidden" : ""}`}
          >
            {children}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/90 via-amber-50/30 to-stone-100 pb-28 md:pb-10">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-5 sm:py-8 lg:px-6">
        {/* Top bar */}
        <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Link
              to={shopPath}
              className="inline-flex items-center gap-1 text-sm font-semibold text-orange-700 transition hover:text-orange-900"
            >
              <span aria-hidden>←</span> Back to menu
            </Link>
            <h1 className="mt-3 flex flex-wrap items-center gap-2 text-2xl font-extrabold tracking-tight text-stone-900 sm:text-3xl">
              Checkout
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-0.5 text-xs font-bold text-orange-700 shadow-sm ring-1 ring-orange-200/80">
                <Sparkles className="h-3.5 w-3.5" />
                Fast checkout
              </span>
            </h1>
            <p className="mt-1 max-w-xl text-sm text-stone-600 sm:text-base">
              {itemCount} {itemCount === 1 ? "item" : "items"} in your bag · Secure payments · Track
              after you order
            </p>
          </div>
          <div className="hidden items-center gap-2 rounded-2xl border border-emerald-200/80 bg-emerald-50/90 px-4 py-2.5 text-sm text-emerald-900 shadow-sm sm:flex">
            <Clock className="h-4 w-4 shrink-0 text-emerald-600" />
            <span className="font-medium">Typical prep &amp; dispatch updates after payment</span>
          </div>
          <p className="flex items-center gap-2 text-xs text-emerald-800 sm:hidden">
            <Clock className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
            <span>Order updates after checkout</span>
          </p>
        </div>

        {(fulfillmentMeta.label || fulfillmentMeta.description) && (
          <div className="mb-4 flex gap-3 rounded-2xl border border-amber-200/90 bg-gradient-to-r from-amber-50/95 to-orange-50/70 px-4 py-3 shadow-sm ring-1 ring-amber-100/70 sm:mb-6">
            <Truck
              className="mt-0.5 h-5 w-5 shrink-0 text-amber-700"
              strokeWidth={2}
              aria-hidden
            />
            <p className="text-sm leading-snug text-stone-800">
              {fulfillmentMeta.description ? (
                <>
                  {fulfillmentMeta.label ? (
                    <span className="font-bold text-stone-900">
                      {fulfillmentMeta.label}
                      <span className="font-semibold text-stone-600"> — </span>
                    </span>
                  ) : null}
                  <span>{fulfillmentMeta.description}</span>
                </>
              ) : (
                <span className="font-semibold text-stone-900">{fulfillmentMeta.label}</span>
              )}
            </p>
          </div>
        )}

        {/* Mobile: tap steps to jump between sections (sits above thumb zone) */}
        <nav
          className="mb-4 flex items-stretch justify-between gap-2 rounded-2xl border border-stone-200/90 bg-white/95 p-2 shadow-sm md:hidden"
          aria-label="Checkout steps"
        >
          {[
            { key: "order", label: "Bag", Icon: ShoppingBag },
            { key: "shipping", label: "Address", Icon: MapPin },
            { key: "payment", label: "Pay", Icon: CreditCard },
          ].map(({ key, label, Icon }) => {
            const active = activeSection === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveSection(key)}
                className={`flex min-h-[48px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1.5 text-[11px] font-bold transition ${
                  active
                    ? "bg-orange-500 text-white shadow-md shadow-orange-300/40"
                    : "bg-stone-50 text-stone-600 ring-1 ring-stone-100 hover:bg-stone-100"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </nav>

        {/* Step rail — desktop only (md+ matches main checkout layout) */}
        <div className="mb-6 hidden items-center gap-2 md:flex">
          {steps.map((s, i) => (
            <React.Fragment key={s.key}>
              {i > 0 ? (
                <div
                  className={`h-0.5 flex-1 max-w-[3rem] rounded-full ${
                    steps[i - 1].done ? "bg-orange-400" : "bg-stone-200"
                  }`}
                />
              ) : null}
              <div
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${
                  s.done
                    ? "bg-orange-500 text-white shadow-md shadow-orange-300/40"
                    : s.active
                      ? "bg-orange-100 text-orange-900 ring-2 ring-orange-300"
                      : "bg-white text-stone-500 ring-1 ring-stone-200"
                }`}
              >
                {s.label}
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Mobile (step-by-step) */}
        <div className="md:hidden">
          {activeSection === "order" ? (
            <div className="space-y-3">
              <div ref={orderRef} />
              <OrderSummary
                embedded
                cart={cart}
                updateQuantity={updateQuantity}
                shippingFee={effectiveShippingFee}
                cartShippingFee={shippingFee}
                receivingMethod={receivingMethod}
                gstTotal={gstTotal}
                couponDiscount={effectiveCouponDiscount}
                subtotal={subtotal}
                finalPayableRupees={finalPayableRupees}
              />
              <CouponPage
                subtotal={subtotal}
                shippingFee={effectiveShippingFee}
                gstTotal={gstTotal}
                maxCouponDiscount={maxCouponDiscount}
                setCouponDiscount={setCouponDiscount}
              />

              <button
                type="button"
                onClick={() => setActiveSection("shipping")}
                className="w-full rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-orange-300/45 transition active:scale-[0.98] hover:from-orange-600 hover:to-amber-600"
              >
                Next: {shipStepWord}
              </button>
            </div>
          ) : null}

          {activeSection === "shipping" ? (
            <div className="space-y-3">
              <div ref={shippingRef} />
              <div className="rounded-2xl border border-stone-200/90 bg-white p-4 shadow-md shadow-stone-200/40">
                <p className="text-sm font-extrabold text-stone-900">
                  {shipStepWord} details
                </p>
                <p className="mt-1 text-xs text-stone-500">
                  {customerFulfillmentMode === "pickup"
                    ? "Store pickup · no address"
                    : receivingMethod === "pickup"
                      ? "Self pickup — no address"
                      : form.address
                        ? `${form.city || "Address"} saved`
                        : "Choose or add address"}
                </p>
                <div className="mt-4">
                  {isServiceCart && (
                    <div className="mb-3 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-800">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                      <span>This is a <strong>service package</strong> — the vendor will come to your address. No delivery fee.</span>
                    </div>
                  )}
                  <ShippingInfo
                    form={form}
                    setForm={setForm}
                    onAddressSelected={(address, isExisting) => {
                      setForm(address);
                      setIsNewAddress(!isExisting);
                    }}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={setSelectedIndex}
                    receivingMethod={receivingMethod}
                    onReceivingMethodChange={setReceivingMethod}
                    customerFulfillmentMode={isServiceCart ? "delivery" : customerFulfillmentMode}
                    pickupPointLocation={pickupPointLocation}
                    pickupPointGoogleMapsUrl={pickupPointGoogleMapsUrl}
                    pickupPointLat={pickupPointLat}
                    pickupPointLng={pickupPointLng}
                    isServiceCart={isServiceCart}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setActiveSection("order")}
                  className="flex-1 rounded-2xl border-2 border-stone-200 bg-white py-3 text-sm font-extrabold text-stone-800 transition hover:bg-stone-50 active:scale-[0.98]"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection("payment")}
                  disabled={!canPlaceOrder || !hasPaymentOption}
                  className={`flex-1 rounded-2xl py-3 text-sm font-extrabold shadow-lg transition active:scale-[0.98] ${
                    canPlaceOrder && hasPaymentOption
                      ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-orange-300/45 hover:from-orange-600 hover:to-amber-600"
                      : "cursor-not-allowed bg-stone-200 text-stone-500 shadow-none"
                  }`}
                >
                  Next: Pay
                </button>
              </div>
            </div>
          ) : null}

          {activeSection === "payment" ? (
            <div className="space-y-3">
              <div ref={paymentRef} />
              <div className="rounded-2xl border border-stone-200/90 bg-white p-4 shadow-md shadow-stone-200/40">
                <p className="text-sm font-extrabold text-stone-900">Payment</p>
                <p className="mt-1 text-xs text-stone-500">
                  {paymentMethod === "razorpay" ? "Online" : "Cash on delivery"}
                </p>

                <div className="mt-4">
                  <PaymentMethod
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    codEnabled={codEnabled}
                    razorpayEnabled={checkoutRazorpayEnabled}
                  />
                </div>

                <div className="mt-6 border-t border-stone-100 pt-5">
                  <div className="mb-4 flex items-end justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
                        Total
                      </p>
                      <p className="text-xl font-extrabold tabular-nums text-orange-600">
                        ₹{displayTotal.toFixed(0)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`w-full rounded-2xl py-3.5 text-sm font-bold shadow-lg transition active:scale-[0.98] ${
                      canPlaceOrder && hasPaymentOption
                        ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-orange-300/50 hover:from-orange-600 hover:to-amber-600"
                        : "cursor-not-allowed bg-stone-200 text-stone-500 shadow-none"
                    }`}
                    onClick={
                      paymentMethod === "razorpay"
                        ? handleOnlinePayment
                        : handleCODPayment
                    }
                    disabled={!canPlaceOrder || !hasPaymentOption}
                  >
                    {placeOrderLabel}
                  </button>
                  {!hasPaymentOption ? (
                    <p className="mt-2 text-xs font-semibold text-amber-800">
                      No payment methods are available right now. Please try again later or contact support.
                    </p>
                  ) : null}
                  {!canPlaceOrder ? (
                    <p className="mt-2 text-xs font-semibold text-rose-600">
                      {customerFulfillmentMode === "both"
                        ? "Choose self pickup or add a delivery address"
                        : "Add a delivery address to continue"}
                    </p>
                  ) : null}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setActiveSection("shipping")}
                className="w-full rounded-2xl border-2 border-stone-200 bg-white py-3 text-sm font-extrabold text-stone-800 transition hover:bg-stone-50 active:scale-[0.98]"
              >
                Back
              </button>
            </div>
          ) : null}
        </div>

        {/* Desktop */}
        <div className="hidden gap-8 md:grid md:grid-cols-12 lg:gap-10">
          <div className="md:col-span-7 lg:col-span-8">
            <div className="rounded-3xl border border-stone-200/80 bg-white p-6 shadow-xl shadow-stone-200/50 lg:p-8">
              <div className="mb-6 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500 text-white shadow-md shadow-orange-300/40">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-lg font-bold text-stone-900 sm:text-xl">
                    {customerFulfillmentMode === "pickup"
                      ? "Store pickup"
                      : customerFulfillmentMode === "both"
                        ? "Pickup or delivery"
                        : `${shipStepWord} address`}
                  </h2>
                  <p className="text-sm text-stone-500">
                    {isServiceCart
                      ? "Your address is the service location — the vendor will visit you. No delivery fee."
                      : customerFulfillmentMode === "pickup"
                        ? "This shop only offers in-person pickup. No delivery address or delivery fee."
                        : customerFulfillmentMode === "both"
                          ? "Choose delivery (address required) or self pickup (delivery fee waived)."
                          : "Saved addresses or a new spot — your choice."}
                  </p>
                </div>
              </div>
              {isServiceCart && (
                <div className="mb-4 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                  <span>This is a <strong>service package</strong> — the vendor will come to your address. No delivery fee.</span>
                </div>
              )}
              <ShippingInfo
                form={form}
                setForm={setForm}
                onAddressSelected={(address, isExisting) => {
                  setForm(address);
                  setIsNewAddress(!isExisting);
                }}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                receivingMethod={receivingMethod}
                onReceivingMethodChange={setReceivingMethod}
                customerFulfillmentMode={isServiceCart ? "delivery" : customerFulfillmentMode}
                pickupPointLocation={pickupPointLocation}
                pickupPointGoogleMapsUrl={pickupPointGoogleMapsUrl}
                pickupPointLat={pickupPointLat}
                pickupPointLng={pickupPointLng}
                isServiceCart={isServiceCart}
              />

              <div className="my-8 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent" />

              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-stone-900 text-white">
                  <CreditCard className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-lg font-bold text-stone-900 sm:text-xl">How would you like to pay?</h2>
                  <p className="text-sm text-stone-500">COD or instant online payment.</p>
                </div>
              </div>
              <PaymentMethod
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                codEnabled={codEnabled}
                razorpayEnabled={checkoutRazorpayEnabled}
              />

              <div className="mt-8 flex flex-col gap-4 border-t border-stone-100 pt-6 md:flex-row md:items-start md:justify-between md:gap-6 lg:gap-8">
                <p className="flex min-w-0 flex-1 items-center gap-2.5 text-sm leading-snug text-stone-600">
                  <ShieldCheck
                    className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
                    strokeWidth={2}
                    aria-hidden
                  />
                  <span className="min-w-0 pt-px">
                    256-bit secure checkout{" "}
                    <span className="text-stone-400" aria-hidden>
                      ·
                    </span>{" "}
                    Your details stay private{" "}
                    <span className="text-stone-400" aria-hidden>
                      ·
                    </span>{" "}
                    <Link
                      to="/privacy-policy"
                      className="font-semibold text-orange-700 underline decoration-orange-300 underline-offset-4 hover:text-orange-900"
                    >
                      Privacy policy
                    </Link>
                  </span>
                </p>
                <div className="flex w-full shrink-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end md:w-auto">
                  <Link
                    to={shopPath}
                    className="inline-flex h-11 items-center justify-center rounded-xl border-2 border-stone-200 px-5 text-sm font-bold text-stone-700 transition hover:bg-stone-50 sm:h-12 sm:px-5"
                  >
                    ← Add more items
                  </Link>
                  <div className="group relative w-full sm:w-auto sm:min-w-[11rem]">
                    <button
                      type="button"
                      className={`flex h-11 w-full items-center justify-center rounded-2xl px-6 text-sm font-bold shadow-lg transition active:scale-[0.98] sm:h-12 sm:min-w-[11rem] ${
                        canPlaceOrder && hasPaymentOption
                          ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-orange-300/50 hover:from-orange-600 hover:to-amber-600"
                          : "cursor-not-allowed bg-stone-200 text-stone-500 shadow-none"
                      }`}
                      onClick={
                        paymentMethod === "razorpay"
                          ? handleOnlinePayment
                          : handleCODPayment
                      }
                      disabled={!canPlaceOrder || !hasPaymentOption}
                    >
                      {placeOrderLabel}
                    </button>
                    {!hasPaymentOption ? (
                      <span className="pointer-events-none absolute -top-9 right-0 z-10 max-w-[14rem] whitespace-normal rounded-lg bg-amber-900 px-2 py-1 text-[10px] font-medium text-white opacity-0 shadow-lg transition group-hover:opacity-100">
                        No payment methods available. Contact support.
                      </span>
                    ) : null}
                    {!canPlaceOrder ? (
                      <span className="pointer-events-none absolute -top-9 right-0 z-10 whitespace-nowrap rounded-lg bg-stone-900 px-2 py-1 text-[10px] font-medium text-white opacity-0 shadow-lg transition group-hover:opacity-100">
                        {customerFulfillmentMode === "both"
                          ? "Choose self pickup or add a delivery address"
                          : "Add a delivery address to continue"}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className="md:col-span-5 lg:col-span-4">
            <div className="sticky top-24 space-y-4">
              <OrderSummary
                cart={cart}
                updateQuantity={updateQuantity}
                shippingFee={effectiveShippingFee}
                cartShippingFee={shippingFee}
                receivingMethod={receivingMethod}
                gstTotal={gstTotal}
                couponDiscount={effectiveCouponDiscount}
                subtotal={subtotal}
                finalPayableRupees={finalPayableRupees}
              />
              <CouponPage
                subtotal={subtotal}
                shippingFee={effectiveShippingFee}
                gstTotal={gstTotal}
                maxCouponDiscount={maxCouponDiscount}
                setCouponDiscount={setCouponDiscount}
              />
              <p className="flex items-start gap-2 rounded-2xl border border-stone-200/80 bg-white/80 p-4 text-xs text-stone-600 shadow-sm">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                Orders are confirmed after successful payment (or COD placement). You will receive
                updates on your registered contact.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
